import { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useField } from "formik";
import PhoneInput from "react-phone-number-input";
import InputFormik from "app/pages/.shared/form/InputFormik";
import { FormattedMessage } from "react-intl";
import { datadogRum } from "@datadog/browser-rum";
import "./PhoneIntl.scss";
import PhoneFlag from "app/pages/.shared/profile/PhoneFlag/PhoneFlag";
import appGlobalsContext from "app/AppGlobalsContext";
import classNames from "classnames";

const PhoneIntl = ({
	id,
	name,
	isRequired,
	shop,
	setFieldValue,
	setFieldTouched,
	setFieldError,
	label,
	...restProps
}) => {
	const [field] = useField(name);
	const [lang, country] = shop.split("-");
	const [locale, setLocale] = useState();
	const [hasChanged, setHasChanged] = useState(false);

	const { isRTL } = useContext(appGlobalsContext);

	const handleBlur = () => {
		setHasChanged(false);
		if (typeof restProps?.onBlur === "function") {
			restProps.onBlur();
		}
	};

	const handleChange = useCallback(
		value => {
			setFieldTouched(name);
			setFieldValue(name, value);
			if (value !== "" && !hasChanged) {
				setFieldError(name);
				setHasChanged(true);
			}
		},
		[name, hasChanged]
	);

	useEffect(() => {
		import(/* webpackChunkName: "phone-country-[request]" */
		`/node_modules/react-phone-number-input/locale/${lang}.json`)
			.then(locale => {
				setLocale(locale.default);
			})
			.catch(err => {
				datadogRum.addError(err);
			});
	}, [lang]);

	return (
		<PhoneInput
			id={id}
			name={name}
			className={classNames("phone-intl-input", {
				"phone-intl-input--has-value": field?.value,
			})}
			displayInitialValueAsLocalNumber
			inputComponent={InputFormik}
			defaultCountry={country}
			value={field.value}
			labels={locale}
			flagComponent={PhoneFlag}
			shouldSanitize={false}
			onChange={handleChange}
			isRequired={isRequired}
			numberInputProps={{
				type: "tel",
				label: label,
				maxLength: "50",
				dir: "ltr",
				style: { textAlign: isRTL ? "right" : "left" },
			}}
			onBlur={handleBlur}
			{...restProps}
		/>
	);
};

PhoneIntl.defaultProps = {
	label: <FormattedMessage id="phone.label" />,
};

PhoneIntl.propTypes = {
	id: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	isRequired: PropTypes.bool,
	shop: PropTypes.string,
	setFieldValue: PropTypes.func,
	setFieldTouched: PropTypes.func,
	setFieldError: PropTypes.func,
	label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
};

export default PhoneIntl;
